import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { API } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { AppHeader } from "@/components/AppHeader";
import { CaretRight, CaretDown, Plus, Check, X, PencilSimple, Folder, FolderOpen, Trash, Warning } from "@phosphor-icons/react";

const STATUS_OPTS = [["A", "statusActive"], ["H", "statusHidden"], ["D", "statusDisabled"]];
const STATUS_CLS = { A: "text-emerald-600", H: "text-amber-600", D: "text-muted-foreground" };
const TH = "px-3 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground";

export default function CategoriesPage() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [tree, setTree] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [editing, setEditing] = useState({ id: null, value: "" });
  const [newFor, setNewFor] = useState(null);
  const [newName, setNewName] = useState("");
  const [delCat, setDelCat] = useState(null);
  const [deletingCat, setDeletingCat] = useState(false);

  const countDescendants = (node) => (node.children || []).reduce((n, c) => n + 1 + countDescendants(c), 0);
  const deleteCategory = async () => {
    if (!delCat) return;
    setDeletingCat(true);
    try {
      await axios.delete(`${API}/admin/categories/${delCat.category_id}`);
      toast.success(t.categoryDeleted);
      setDelCat(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || t.saveFailed); } finally { setDeletingCat(false); }
  };

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/categories`).then((r) => { setTree(r.data.tree || []); setTotals(r.data.totals); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setExpanded((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const flatten = (nodes, depth, acc) => {
    for (const n of nodes) {
      acc.push({ node: n, depth });
      if (n.children && n.children.length && expanded.has(n.category_id)) flatten(n.children, depth + 1, acc);
    }
    return acc;
  };
  const rows = flatten(tree, 0, []);

  const saveName = async (node, value) => {
    const v = value.trim();
    setEditing({ id: null, value: "" });
    if (!v) return;
    try {
      await axios.put(`${API}/admin/categories/${node.category_id}`, lang === "en" ? { name_en: v } : { name_fr: v });
      toast.success(t.categorySaved); load();
    } catch (e) { toast.error(e.response?.data?.detail || t.saveFailed); }
  };
  const saveStatus = async (node, status) => {
    try { await axios.put(`${API}/admin/categories/${node.category_id}`, { status }); toast.success(t.categorySaved); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t.saveFailed); }
  };
  const addChild = (parentId) => {
    setNewFor(parentId); setNewName("");
    if (parentId !== "0") setExpanded((s) => new Set(s).add(parentId));
  };
  const createCategory = async () => {
    const v = newName.trim();
    if (!v) return;
    try {
      await axios.post(`${API}/admin/categories`, { name_fr: v, name_en: v, parent_id: newFor });
      toast.success(t.categorySaved); setNewFor(null); setNewName(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || t.saveFailed); }
  };

  const NewInput = ({ testid, indent }) => (
    <div className="flex items-center gap-2" style={{ paddingLeft: indent }}>
      <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") createCategory(); if (e.key === "Escape") setNewFor(null); }}
        data-testid={testid} placeholder={t.newCategoryName} className="flex-1 border border-primary bg-background px-3 py-2 text-sm outline-none" />
      <button data-testid={`${testid}-save`} onClick={createCategory} className="inline-flex items-center gap-1 bg-primary px-3 py-2 text-xs font-bold text-white"><Check size={14} weight="bold" /></button>
      <button onClick={() => setNewFor(null)} className="border border-border px-3 py-2 text-muted-foreground"><X size={14} weight="bold" /></button>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="px-4 sm:px-6 py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">{t.backOffice}</div>
            <h1 className="mt-1 font-display text-4xl font-black tracking-tighter text-foreground">{t.manageCategories}</h1>
          </div>
          <button data-testid="add-root-category" onClick={() => addChild("0")}
            className="inline-flex items-center gap-2 bg-primary px-5 py-3 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5">
            <Plus size={16} weight="bold" /> {t.addCategory}
          </button>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
          <div className="lg:col-span-3">
            {newFor === "0" && (
              <div className="mb-3 border border-primary bg-card p-3" data-testid="new-root-cat-form">
                <NewInput testid="new-cat-input" indent={0} />
              </div>
            )}
            <div className="border border-border bg-card overflow-x-auto">
              <table data-testid="categories-table" className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b-2 border-border text-left">
                    <th className={`${TH} w-16 text-center`}>{t.position}</th>
                    <th className={TH}>{t.name}</th>
                    <th className={`${TH} w-28 text-center`}>{t.products}</th>
                    <th className={`${TH} w-40 text-right`}>{t.status}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">{t.loading}</td></tr>}
                  {!loading && rows.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">{t.noCategories}</td></tr>}
                  {!loading && rows.map(({ node, depth }) => {
                    const hasKids = node.children && node.children.length > 0;
                    const isOpen = expanded.has(node.category_id);
                    const isEditing = editing.id === node.category_id;
                    return (
                      <React.Fragment key={node.category_id}>
                        <tr data-testid={`cat-row-${node.category_id}`} className="border-b border-border hover:bg-muted/40 transition-colors duration-150">
                          <td className="px-3 py-2.5 text-center font-mono text-xs text-muted-foreground">{node.position || 0}</td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-2" style={{ paddingLeft: depth * 22 }}>
                              {hasKids && (
                                <button data-testid={`cat-toggle-${node.category_id}`} onClick={() => toggle(node.category_id)} className="text-primary hover:opacity-70">
                                  {isOpen ? <CaretDown size={15} weight="bold" /> : <CaretRight size={15} weight="bold" />}
                                </button>
                              )}
                              {!hasKids && <span className="inline-block w-[15px]" />}
                              {hasKids && isOpen && <FolderOpen size={16} weight="bold" className="text-muted-foreground" />}
                              {hasKids && !isOpen && <Folder size={16} weight="bold" className="text-muted-foreground" />}
                              {!hasKids && <span className="inline-block w-4" />}
                              {isEditing && (
                                <span className="flex items-center gap-1">
                                  <input autoFocus data-testid={`cat-name-input-${node.category_id}`} value={editing.value}
                                    onChange={(e) => setEditing({ id: node.category_id, value: e.target.value })}
                                    onKeyDown={(e) => { if (e.key === "Enter") saveName(node, editing.value); if (e.key === "Escape") setEditing({ id: null, value: "" }); }}
                                    className="border border-primary bg-background px-2 py-1 text-sm outline-none" />
                                  <button data-testid={`cat-save-${node.category_id}`} onClick={() => saveName(node, editing.value)} className="text-emerald-600 hover:opacity-70"><Check size={16} weight="bold" /></button>
                                  <button onClick={() => setEditing({ id: null, value: "" })} className="text-muted-foreground hover:opacity-70"><X size={16} weight="bold" /></button>
                                </span>
                              )}
                              {!isEditing && (
                                <button data-testid={`cat-name-${node.category_id}`} onClick={() => setEditing({ id: node.category_id, value: node.name[lang] || node.name.fr || "" })}
                                  className="group inline-flex items-center gap-1.5 text-left text-sm font-medium text-foreground hover:text-primary">
                                  {node.name[lang] || node.name.fr}
                                  <PencilSimple size={12} weight="bold" className="opacity-0 group-hover:opacity-60" />
                                </button>
                              )}
                              <button data-testid={`cat-add-sub-${node.category_id}`} onClick={() => addChild(node.category_id)} title={t.addSubcategory}
                                className="ml-1 text-muted-foreground hover:text-primary"><Plus size={13} weight="bold" /></button>
                              <button data-testid={`cat-delete-${node.category_id}`} onClick={() => setDelCat(node)} title={t.deleteCategory}
                                className="ml-0.5 text-muted-foreground hover:text-red-500"><Trash size={13} weight="bold" /></button>
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            <button type="button" data-testid={`cat-count-${node.category_id}`}
                              onClick={() => navigate(`/admin/products?category=${node.category_id}`)}
                              title={t.viewCategoryProducts}
                              className="inline-flex min-w-[42px] items-center justify-center bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary transition-colors duration-150 hover:bg-primary hover:text-white">{node.product_count || 0}</button>
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            <select data-testid={`cat-status-${node.category_id}`} value={node.status || "A"} onChange={(e) => saveStatus(node, e.target.value)}
                              className={`border border-border bg-background px-2 py-1 text-xs font-bold outline-none focus:border-primary ${STATUS_CLS[node.status || "A"]}`}>
                              {STATUS_OPTS.map(([v, k]) => <option key={v} value={v}>{t[k]}</option>)}
                            </select>
                          </td>
                        </tr>
                        {newFor === node.category_id && (
                          <tr data-testid={`new-sub-form-${node.category_id}`}>
                            <td></td>
                            <td colSpan={3} className="px-3 py-2">
                              <NewInput testid={`new-sub-input-${node.category_id}`} indent={(depth + 1) * 22} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="border border-border bg-card p-5" data-testid="categories-totals">
              <div className="mb-4 font-display text-lg font-black tracking-tight text-foreground">{t.catalogTotals}</div>
              {[
                [t.categories, totals && totals.categories],
                [t.totalProducts, totals && totals.products],
                [t.activeCategories, totals && totals.active],
                [t.hiddenCategories, totals && totals.hidden],
                [t.disabledCategories, totals && totals.disabled],
              ].map((pair, i) => (
                <div key={i} className="flex items-center justify-between border-b border-border py-2.5 text-sm last:border-0">
                  <span className="text-muted-foreground">{pair[0]}</span>
                  <span className="font-bold tabular-nums text-foreground" data-testid={`total-${i}`}>{pair[1] == null ? "—" : pair[1]}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {delCat && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="delete-category-modal">
          <div className="w-full max-w-md border border-border bg-card p-6 shadow-xl">
            <div className="mb-4 flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600"><Warning size={22} weight="bold" /></div>
              <div>
                <h2 className="font-display text-lg font-black tracking-tight text-foreground">{t.deleteCategoryTitle}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{t.deleteCategoryWarning}</p>
              </div>
            </div>
            <div className="mb-5 border border-border bg-muted/40 px-3 py-2.5">
              <div className="text-sm font-semibold text-foreground">{delCat.name?.[lang] || delCat.name?.fr}</div>
              {countDescendants(delCat) > 0 && (
                <div className="mt-1 text-[11px] font-semibold text-amber-600">{countDescendants(delCat)} {t.subcategoriesAlsoDeleted}</div>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <button data-testid="delete-category-cancel" onClick={() => setDelCat(null)} disabled={deletingCat}
                className="border border-border px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-foreground hover:bg-muted disabled:opacity-60">{t.cancel}</button>
              <button data-testid="delete-category-confirm" onClick={deleteCategory} disabled={deletingCat}
                className="inline-flex items-center gap-2 bg-red-600 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-red-700 disabled:opacity-60">
                <Trash size={14} weight="bold" /> {t.deletePermanently}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
